import React, { useState, useEffect } from 'react';
import apiClient, { getErrorMessage } from '../utils/api';
import './LoanDetailsDialog.css';

interface LoanDetail {
  acctrefno: number;
  loan_number: string;
  customer_name: string;
  email_address: string;
  principal_balance: number;
  interest_balance: number;
  payoff_balance: number;
  days_past_due: number;
  last_extension_date: string;
  deferment_count: number;
  last_promise_date: string;
  last_promise_amount: string;
  last_payment_date: string;
  last_payment_amount: string;
  account_manager: string;
  // cease_and_desist: string;
  loan_group: string;
}

interface VehicleDetail {
  loan_number: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  style: string;
  original_miles: number;
  current_miles: number;
  original_value: number;
  current_value: number;
  valuation_method: string;
  valuation_date: string;
  comments: string;
}

interface LoanDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  loanDetails: LoanDetail | null;
  loading?: boolean;
  error?: string | null;
}

const LoanDetailsDialog: React.FC<LoanDetailsDialogProps> = ({
  isOpen,
  onClose,
  loanDetails,
  loading = false,
  error = null
}) => {
  const [vehicleDetails, setVehicleDetails] = useState<VehicleDetail | null>(null);
  const [vehicleAccordionOpen, setVehicleAccordionOpen] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  
  // Comment section state
  const [commentAccordionOpen, setCommentAccordionOpen] = useState(false);
  const [commentCategories, setCommentCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [commentDescription, setCommentDescription] = useState('');
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentSuccess, setCommentSuccess] = useState(false);

  // Reset state when dialog closes or loan changes
  useEffect(() => {
    if (!isOpen) {
      setVehicleDetails(null);
      setVehicleAccordionOpen(false);
      setCommentAccordionOpen(false);
      setSelectedCategory('');
      setCommentDescription('');
      setCommentText('');
      setCommentError(null);
      setCommentSuccess(false);
    }
  }, [isOpen, loanDetails]);

  // Fetch comment categories when accordion opens
  useEffect(() => {
    if (commentAccordionOpen && commentCategories.length === 0 && !loadingCategories) {
      fetchCommentCategories();
    }
  }, [commentAccordionOpen]);

  const fetchVehicleDetails = async () => {
    if (!loanDetails) return;
    
    // Toggle accordion if we already have the details
    if (vehicleDetails) {
      setVehicleAccordionOpen(!vehicleAccordionOpen);
      return;
    }

    try {
      setLoadingVehicle(true);
      const response = await apiClient.get(`/port-health/vehicle-details/${encodeURIComponent(loanDetails.loan_number)}`);
      setVehicleDetails(response.data);
      setVehicleAccordionOpen(true);
    } catch (err) {
      console.error('Error fetching vehicle details:', err);
      // We don't block the main dialog if vehicle details fail
    } finally {
      setLoadingVehicle(false);
    }
  };

  const fetchCommentCategories = async () => {
    try {
      setLoadingCategories(true);
      setCommentError(null); // Clear any previous errors
      const response = await apiClient.get('/port-health/comment-categories');
      setCommentCategories(response.data.categories || []);
      if (response.data.categories && response.data.categories.length === 0) {
        setCommentError('No comment categories found in database');
      }
    } catch (err: any) {
      console.error('Error fetching comment categories:', err);
      const errorMsg = err?.response?.data?.error || err?.response?.data?.details || 'Failed to load comment categories';
      setCommentError(errorMsg);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCommentToggle = () => {
    setCommentAccordionOpen(!commentAccordionOpen);
    if (!commentAccordionOpen && commentCategories.length === 0) {
      fetchCommentCategories();
    }
  };

  const handleSaveComment = async () => {
    if (!loanDetails) return;
    
    if (!selectedCategory) {
      setCommentError('Please select a category');
      return;
    }
    
    if (!commentText.trim()) {
      setCommentError('Please enter a comment');
      return;
    }

    try {
      setSavingComment(true);
      setCommentError(null);
      setCommentSuccess(false);

      const response = await apiClient.post('/port-health/save-comment', {
        loan_number: loanDetails.loan_number,
        category: selectedCategory,
        comment_description: commentDescription,
        comment_text: commentText
      });

      if (response.data.success) {
        setCommentSuccess(true);
        // Reset form
        setSelectedCategory('');
        setCommentDescription('');
        setCommentText('');
        // Close accordion after a short delay
        setTimeout(() => {
          setCommentAccordionOpen(false);
          setCommentSuccess(false);
        }, 2000);
      }
    } catch (err: any) {
      console.error('Error saving comment:', err);
      setCommentError(getErrorMessage(err));
    } finally {
      setSavingComment(false);
    }
  };

  const handleCancelComment = () => {
    setSelectedCategory('');
    setCommentDescription('');
    setCommentText('');
    setCommentError(null);
    setCommentSuccess(false);
    setCommentAccordionOpen(false);
  };

  if (!isOpen) return null;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'N/A') return dateString;
    
    // Handle YYYY/mm/dd format
    if (dateString.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
      const [year, month, day] = dateString.split('/');
      return `${month}/${day}/${year}`;
    }
    
    // Return as-is if already in other format
    return dateString;
  };

  const getDaysPastDueBadgeClass = (days: number): string => {
    if (days === 0) return 'current';
    if (days < 10) return 'slightly-past-due';
    if (days < 30) return 'moderately-past-due';
    return 'severely-past-due';
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-container loan-details-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Loan Details</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="dialog-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading loan details...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p className="error-message">{error}</p>
            </div>
          ) : loanDetails ? (
            <div className="loan-details-content">
              <div className="loan-header">
                <h3>Loan #{loanDetails.loan_number}</h3>
                <div className="customer-name">
                  <strong>{loanDetails.customer_name}</strong>
                </div>
              </div>

              <div className="loan-details-grid">
                <div className="detail-item">
                  <label>Loan Number:</label>
                  <span className="loan-number">{loanDetails.loan_number}</span>
                </div>

                <div className="detail-item">
                  <label>Customer Name:</label>
                  <span className="customer-name">{loanDetails.customer_name}</span>
                </div>

                <div className="detail-item">
                  <label>Email Address:</label>
                  <span className="email-address">{loanDetails.email_address || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Account Manager:</label>
                  <span className="account-manager">{loanDetails.account_manager || 'N/A'}</span>
                </div>

                {/* <div className="detail-item">
                  <label>Cease and Desist:</label>
                  <span className="cease-desist">{formatDate(loanDetails.cease_and_desist || 'N/A')}</span>
                </div> */}

                <div className="detail-item">
                  <label>Loan Group:</label>
                  <span className="loan-group">{loanDetails.loan_group || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Principal Balance:</label>
                  <span className="balance principal">{formatCurrency(loanDetails.principal_balance)}</span>
                </div>

                <div className="detail-item">
                  <label>Interest Balance:</label>
                  <span className="balance interest">{formatCurrency(loanDetails.interest_balance)}</span>
                </div>

                <div className="detail-item">
                  <label>Payoff Balance:</label>
                  <span className="balance payoff">{formatCurrency(loanDetails.payoff_balance)}</span>
                </div>

                <div className="detail-item">
                  <label>Days Past Due:</label>
                  <span className={`days-badge ${getDaysPastDueBadgeClass(loanDetails.days_past_due)}`}>
                    {loanDetails.days_past_due}
                  </span>
                </div>


                <div className="detail-item">
                  <label>Last Extension Date:</label>
                  <span className="last-extension-date">{loanDetails.last_extension_date || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Deferment Count:</label>
                  <span className="deferment-count">{loanDetails.deferment_count}</span>
                </div>

                <div className="detail-item">
                  <label>Last Promise Date:</label>
                  <span className="last-promise-date">{loanDetails.last_promise_date || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Last Promise Amount:</label>
                  <span className="last-promise-amount">{loanDetails.last_promise_amount || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Last Payment Date:</label>
                  <span className="last-payment-date">{loanDetails.last_payment_date || 'N/A'}</span>
                </div>

                <div className="detail-item">
                  <label>Last Payment Amount:</label>
                  <div className="payment-amount-container">
                    <span className="last-payment-amount">{loanDetails.last_payment_amount || 'N/A'}</span>
                    <button 
                      className={`collateral-toggle-button ${vehicleAccordionOpen ? 'active' : ''}`}
                      onClick={fetchVehicleDetails}
                      disabled={loadingVehicle}
                      title="View Vehicle Collateral"
                    >
                      {loadingVehicle ? '...' : (vehicleAccordionOpen ? '▲ Vehicle' : '▼ Vehicle')}
                    </button>
                  </div>
                </div>

                <div className="detail-item">
                  <label>Comment:</label>
                  <button 
                    className={`comment-toggle-button ${commentAccordionOpen ? 'active' : ''}`}
                    onClick={handleCommentToggle}
                    title="Add Comment"
                  >
                    {commentAccordionOpen ? '▲ Comment' : '▼ Comment'}
                  </button>
                </div>
              </div>

              {/* Vehicle Collateral Accordion */}
              {vehicleAccordionOpen && (
                <div className="collateral-accordion">
                  <div className="accordion-header">
                    <h4>🚗 Vehicle Collateral Details</h4>
                  </div>
                  {vehicleDetails && Object.keys(vehicleDetails).length > 0 ? (
                    <div className="accordion-content">
                      <div className="collateral-grid">
                        <div className="collateral-item">
                          <label>VIN:</label>
                          <span>{vehicleDetails.vin || 'N/A'}</span>
                        </div>
                        <div className="collateral-item">
                          <label>Year/Make/Model:</label>
                          <span>{vehicleDetails.year} {vehicleDetails.make} {vehicleDetails.model}</span>
                        </div>
                        <div className="collateral-item">
                          <label>Style:</label>
                          <span>{vehicleDetails.style || 'N/A'}</span>
                        </div>
                        <div className="collateral-item">
                          <label>Miles (Orig/Curr):</label>
                          <span>{vehicleDetails.original_miles.toLocaleString()} / {vehicleDetails.current_miles.toLocaleString()}</span>
                        </div>
                        <div className="collateral-item">
                          <label>Value (Orig/Curr):</label>
                          <span className="value-high">{formatCurrency(vehicleDetails.original_value)} / {formatCurrency(vehicleDetails.current_value)}</span>
                        </div>
                        <div className="collateral-item">
                          <label>Valuation:</label>
                          <span>{vehicleDetails.valuation_method} ({vehicleDetails.valuation_date})</span>
                        </div>
                        <div className="collateral-item full-width">
                          <label>Comments:</label>
                          <span className="comments-text">{vehicleDetails.comments || 'No comments'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="accordion-content empty">
                      <p>No vehicle collateral records found for this loan.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Comment Accordion */}
              {commentAccordionOpen && (
                <div className="comment-accordion">
                  <div className="accordion-header">
                    <h4>💬 Add Comment</h4>
                  </div>
                  <div className="accordion-content">
                    {loadingCategories ? (
                      <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading categories...</p>
                      </div>
                    ) : (
                      <div className="comment-form">
                        <div className="form-group">
                          <label htmlFor="comment-category">Category: <span className="required">*</span></label>
                          <select
                            id="comment-category"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="form-select"
                            disabled={savingComment}
                          >
                            <option value="">Select a category</option>
                            {commentCategories.map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="comment-description">Comment Description:</label>
                          <input
                            id="comment-description"
                            type="text"
                            value={commentDescription}
                            onChange={(e) => setCommentDescription(e.target.value)}
                            className="form-input"
                            placeholder="Enter comment description (optional)"
                            disabled={savingComment}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="comment-text">Comment: <span className="required">*</span></label>
                          <textarea
                            id="comment-text"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            className="form-textarea"
                            rows={5}
                            placeholder="Enter your comment here..."
                            disabled={savingComment}
                          />
                        </div>

                        {commentError && (
                          <div className="error-message" style={{ marginTop: '10px' }}>
                            <strong>Error:</strong> {commentError}
                            {commentError.includes('table not found') && (
                              <div style={{ marginTop: '8px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                Please ensure the <code>comment_categories</code> table exists in your database.
                              </div>
                            )}
                          </div>
                        )}

                        {commentSuccess && (
                          <div className="success-message">
                            ✓ Comment saved successfully!
                          </div>
                        )}

                        <div className="form-actions">
                          <button
                            className="save-button"
                            onClick={handleSaveComment}
                            disabled={savingComment || !selectedCategory || !commentText.trim()}
                          >
                            {savingComment ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="cancel-button"
                            onClick={handleCancelComment}
                            disabled={savingComment}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="loan-summary">
                <div className="summary-item">
                  <span className="summary-label">Total Outstanding:</span>
                  <span className="summary-value">
                    {formatCurrency(loanDetails.principal_balance + loanDetails.interest_balance)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p>No loan details available</p>
            </div>
          )}
        </div>
        
        <div className="dialog-footer">
          <button className="close-dialog-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoanDetailsDialog;
